import React, { FC } from 'react';
import { Image, Source } from '@unpic/react';
import { Link } from 'gatsby';
import {
  Button, type ButtonProps,
  useBreakpoint,
} from '@printdeal/ui-library';
import { handleSupplementalBannerClick } from './SupplementalBanner.events';
import { HomePageBannerData } from '../types';
import { AudiencesData } from '../../../../templates/homepage/homepage-helper';
import { getFromLocalStorage, LS_KEYS } from '../../../../helpers/window';

interface SupplementalBannerProps {
  bannerData: HomePageBannerData | undefined,
  audiencesDataFromSegment?: AudiencesData;
  bannerIndex?: number,
}

const SupplementalBanner: FC<SupplementalBannerProps> = ({
  bannerData,
  audiencesDataFromSegment,
  bannerIndex = 0,
}) => {
  const isMobile = useBreakpoint('max-lg');

  const customerAudienceType = getFromLocalStorage(LS_KEYS.AUDIENCE_TYPE)
    || audiencesDataFromSegment?.type;

  if (!bannerData) return null;
  const {
    buttonKind = 'secondary',
    buttonUrl,
    buttonText,
    mediaDesktop,
    mediaMobile,
    text,
  } = bannerData;
  const img = isMobile ? mediaMobile : mediaDesktop;

  if (!img?.url) {
    return null;
  }

  const handleClick = () => {
    handleSupplementalBannerClick(customerAudienceType, bannerData.displayTitle || '', bannerIndex);
  };

  return (
    <div
      data-testid="supplemental-banner"
      className="
        pd-relative pd-flex pd-flex-col pd-items-center pd-justify-between pd-gap-5 pd-rounded-md pd-bg-dim-100 md:pd-grow md:pd-flex-row md:pd-p-0"
    >
      {img && (
        /**
         * Support for modern image formats
         * 19th nov, the supplementary banner images both for printdeal-be & dwd-nl are weighed ~130kb,
         * by requesting images with w=130 as query param in the request to contentful,
         * we can reduce the image size by at least ~ 80%
         */
        <picture>
          {['avif', 'webp', null].map((format) => (
            <Source
              width={130}
              height={130}
              key={format || 'default'}
              type={format && `image/${format}`}
              src={`${img.url}?fm=${format || ''}&w=130`}
            />
          ))}
          <Image
            width={130}
            height={130}
            src={`${img.url}?w=130`}
            className="-pd-mt-5 pd-block md:pd-self-end"
          />
        </picture>
      )}
      {text?.text && (
        <p className="pd-px-3 pd-font-semibold">
          {text.text}
        </p>
      )}
      {buttonText ? (
        <Button
          as={Link}
          to={buttonUrl}
          variant={buttonKind.toLowerCase() as ButtonProps['variant']}
          className="pd-m-3"
          onClick={handleClick}
        >
          {buttonText}
        </Button>
      ) : (
        <Button
          as={Link}
          to={buttonUrl}
          variant="white-circle"
          className="pd-m-3 pd-text-dim-800"
          onClick={handleClick}
          iconBefore="arrow-right"
          screenReaderLabel="white-circle"
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};

export default SupplementalBanner;
